// ข้อมูลรวมของโต๊ะที่ Main cashier ดันขึ้น cloud ให้แอป Flutter (mobile-order) เห็นตรงกับ POS
//
// Flutter อ่านคนละที่กันตามหน้า:
//   หน้ารายการโต๊ะ   → Firestore stores/{id}/sync_data/{table_meta, table_state, live}
//   หน้ารายละเอียดโต๊ะ → RTDB stores/{id}/tables/{table}, tableState/{table}, tableMeta/{table}
// ใช้ร่วมกันระหว่าง mobile.items / tableMeta plugin กับ useCloudSync

import _ from "lodash";
import dayjs from "dayjs";
import { setDoc } from "firebase/firestore";
import { set } from "firebase/database";
import { FIRESTORE_COLLECTIONS } from "~~/interface/FirestoreCollections";
import packageData from "~/package.json";
import { readComputerIdentity } from "~/modules/core/composables/useComputerIdentity";
import { computeOpenTables, computeReceiptTotals } from "~~/modules/pos/utils/storeStatus";

function syncDocRef(docId: string) {
  return useFirebaseFirestore().subCollectionDocIdRef(FIRESTORE_COLLECTIONS.SYNC_DATA, docId);
}

/** tableMeta ทั้งร้าน (ยอดเงิน/จำนวนรายการต่อโต๊ะ) — ใช้ร่วมกับ tableMeta.plugin */
export function buildTableMeta(jobsStore = useJobsStore()) {
  const tableData = jobsStore.$state.tables; // start date, end date, items
  const tableCalculated = jobsStore.$state.tables_v2; // money summary , rounding

  const res = {};
  _.forEach(tableData, (table: any, index) => {
    // tables_v2 คำนวณแบบ debounce อาจยังไม่มีโต๊ะที่เพิ่งเปิด
    const { subTotal, dc1, sc, dc2, cc, tax, rounding, grandTotal } = tableCalculated?.[index] || ({} as any);

    res[index] = {
      guests: table.guests || 2,
      startDate: table.startDate ?? null,
      subTotal: subTotal ?? 0,
      dc1: dc1 ?? 0,
      sc: sc ?? 0,
      dc2: dc2 ?? 0,
      cc: cc ?? 0,
      tax: tax ?? 0,
      rounding: rounding ?? 0,
      grandTotal: grandTotal ?? 0,
      itemsLength: table.items?.length || 0,
    };
  });
  return res;
}

/** สรุป live (จำนวนโต๊ะ/แขก/ยอด + เครื่องไหนเขียน) — ใช้ร่วมกับ mobile.items.plugin */
export function buildLiveSummary(jobsStore = useJobsStore()) {
  const jobs = jobsStore.$state.jobs || [];
  const lastJob = jobs[jobs.length - 1];
  const lastupdate = lastJob?.timestamp || dayjs().toISOString();

  // ยอดที่จ่ายแล้ว = grand total ของบิลที่ไม่ถูก void (ของเดิมใช้ paidAmount จาก PAY job
  // ซึ่งนับบิลที่ void ไปแล้วด้วย) — กติกาจับคู่ PAY job กับใบเสร็จอยู่ใน utils/storeStatus.ts
  const { paid: grandTotal } = computeReceiptTotals(jobs, useReceiptsStore().receipts);

  // ติดป้ายว่า "ใครเป็นคนเขียน live รอบนี้" — main cashier ควรมีเครื่องเดียว
  // ถ้า computerId ใน doc นี้สลับไปมา แปลว่ามีเครื่องแอบตั้งโหมดซ้ำอยู่
  const computerId = useAuthStore().computerUuid || null;
  // ชื่อ/วันสร้าง ID/เวลาเริ่ม session — ID สั้นๆ อย่างเดียวคนหน้าร้านไม่รู้ว่าเครื่องไหน
  const identity = readComputerIdentity();
  const computerName = identity?.name || null;
  const computerCreatedAt = identity?.createdAt || null;
  const sessionStartedAt = useShopStore().presenceStartedAt || null;

  // totalAmount = ยอดค้างของโต๊ะที่เปิดอยู่หลัง sc/ส่วนลด/ภาษี/ปัดเศษ (tables_v2.grandTotal)
  //   ของเดิมรวม item.amount ดิบ ๆ ยังไม่คิด service charge / ภาษี / ปัดเศษ
  // totalGuests = แขกจริงของโต๊ะ (tableState ก่อน แล้วค่อย jobchain)
  //   ของเดิมรวม table.guests ตรง ๆ ADD ที่ไม่ได้ถามจำนวนแขกส่ง -1 มาเลยไปหักยอดรวม
  const { tables, guests, pending } = computeOpenTables(
    jobsStore.$state.tables,
    jobsStore.$state.tables_v2,
    usePosStore().tableState
  );

  return {
    totalTables: tables,
    totalGuests: guests,
    totalAmount: pending,
    lastupdate,
    grandTotal,
    jobCountItems: jobs.length,
    computerId,
    computerName,
    computerCreatedAt,
    sessionStartedAt,
    appVersion: packageData.version,
  };
}

// กันยอดของรอบที่ปิดไปแล้วถูกเขียนกลับขึ้น cloud — ไม่เกี่ยวกับ items บนโต๊ะ
// (ปิดร้านได้ต้องเคลียร์โต๊ะหมดแล้ว)
//
// ลำดับใน close.vue: clearJobs() -> resetLiveSummary() (live = 0) -> email/alert
// -> reload หน้า (~30 วิ) ช่วงก่อน reload listener ทุกตัวยังทำงานอยู่ เช่น
//   - jobs = [] ทำให้ tables recompute -> mobile.items.plugin สร้าง live ใหม่
//     จาก receipts ของวันที่ยังค้างใน memory -> grandTotal 12500 ทับค่า 0
//   - terminal ที่ยังเปิดอยู่ส่ง ADD มาช้า -> totalTables: 1 ทั้งที่ปิดร้านแล้ว
//   - เน็ตกลับมา -> syncAllToCloud() push ทั้งก้อนซ้ำ
// ถ้าปล่อยไว้ มือถือเจ้าของร้านจะเห็นยอดเก่าค้างทั้งคืน (reload แล้วไม่มี job ใหม่
// มา trigger ให้เขียนทับอีก) จึงตั้ง flag นี้ตอนล้าง แล้วทุกจุดที่เขียน live ข้ามไป
// อยู่ใน memory เท่านั้น — reload แล้วกลับเป็น false เอง (ตอนนั้น jobchain ว่างแล้ว)
let liveSyncSuspended = false;

export function isLiveSyncSuspended() {
  return liveSyncSuspended;
}

/**
 * ปิดร้าน: ลบ RTDB stores/{id}/live และรีเซ็ต Firestore sync_data/live เป็นศูนย์
 * (ของเดิมไม่เคยล้าง มือถือเห็นยอด/จำนวนโต๊ะของรอบที่ปิดไปแล้วค้างจนกว่าจะเปิดรอบใหม่)
 * throw ถ้าเขียนไม่สำเร็จ — ผู้เรียกต้องกลืนเอง (ดู useStoreStatus.markStoreStatusClosed)
 */
export async function resetLiveSummary() {
  liveSyncSuspended = true;
  const now = dayjs().toISOString();
  const live = {
    totalTables: 0,
    totalGuests: 0,
    totalAmount: 0,
    lastupdate: now,
    grandTotal: 0,
    jobCountItems: 0,
    appVersion: packageData.version,
  };
  await Promise.all([
    set(useFirebase()._createRef("live"), null),
    // merge: ตัวเลขเป็นศูนย์ ส่วนป้ายเครื่องที่เขียน (computerId/computerName) คงไว้ให้ support ดู
    setDoc(syncDocRef(FIRESTORE_COLLECTIONS.LIVE), live, { merge: true }),
  ]);
}

/** เขียน table_meta / table_state / live ขึ้น Firestore ทั้งก้อน — throw ถ้าไม่สำเร็จ (ปุ่ม Sync to Cloud ต้องรู้ผล) */
export async function writeAllTableDataToFirestore(
  data: { tableMeta?: Record<string, any>; tableState?: Record<string, any>; live?: Record<string, any> } = {}
) {
  const jobsStore = useJobsStore();
  const posStore = usePosStore();

  await Promise.all([
    setDoc(syncDocRef(FIRESTORE_COLLECTIONS.TABLE_META), data.tableMeta ?? buildTableMeta(jobsStore)),
    // set ไม่ merge — โต๊ะที่ปิดไปแล้วต้องหลุดออกจาก doc
    setDoc(syncDocRef(FIRESTORE_COLLECTIONS.TABLE_STATE), data.tableState ?? (posStore.tableState || {})),
    setDoc(syncDocRef(FIRESTORE_COLLECTIONS.LIVE), data.live ?? buildLiveSummary(jobsStore), { merge: true }),
  ]);
}
